import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import styled from 'styled-components';
import { ErrorBoundary } from 'react-error-boundary';
import { LuTriangleAlert } from 'react-icons/lu';
import { TbVideo } from 'react-icons/tb';

import { useVideoProjectCreation } from '@contexts/VideoProjectCreation.context';
import { extractVideoMetadata, formatFileSize } from '@utils/video/helpers';
import CanvasOverlay from './CanvasOverlay';
import TimeRangeSelector from './TimeRangeSelector';

const VideoCanvasWrapper = styled.div`
	position: relative;
	width: 100%;
	background: #000;
	border-radius: 0.375rem;
	overflow: hidden;
`;

const StyledVideo = styled.video`
	width: 100%;
	display: block;
	max-height: 500px;

	/* Chromium-only: hides fullscreen and overflow menu buttons.
	   Firefox does not support these pseudo-selectors — buttons remain visible but functional. */
	&::-webkit-media-controls-fullscreen-button,
	&::-webkit-media-controls-overflow-button {
		display: none;
	}
`;

const VideoError = styled.div`
	padding: 2rem;
	text-align: center;
	background: rgba(220, 53, 69, 0.1);
	border: 1px solid rgba(220, 53, 69, 0.3);
	border-radius: 0.375rem;

	p {
		color: #dc3545;
		margin-bottom: 1rem;
	}
`;

const ErrorIcon = styled.div`
	margin-bottom: 1rem;
	display: flex;
	align-items: center;
	justify-content: center;
	color: #dc3545;
`;

const ErrorTitle = styled.h3`
	color: #dc3545;
	margin-bottom: 0.5rem;
	font-size: 1.25rem;
`;

const ErrorMessage = styled.p`
	color: rgba(255, 255, 255, 0.8);
	margin-bottom: 1rem;
	font-size: 1rem;
`;

const ErrorHint = styled.div`
	background: rgba(255, 193, 7, 0.1);
	border: 1px solid rgba(255, 193, 7, 0.3);
	border-radius: 0.375rem;
	padding: 1rem;
	margin: 1rem 0;
	color: rgba(255, 255, 255, 0.9);
	font-size: 0.875rem;

	strong {
		color: #ffc107;
		display: block;
		margin-bottom: 0.5rem;
	}

	ul {
		margin: 0;
		padding-left: 1.5rem;
	}

	li {
		margin: 0.25rem 0;
	}
`;

const FileInfoBox = styled.div`
	background: rgba(0, 0, 0, 0.3);
	border-radius: 0.375rem;
	padding: 1rem;
	margin-top: 1rem;
	text-align: left;
`;

const FileInfoItem = styled.div`
	padding: 0.5rem 0;
	color: rgba(255, 255, 255, 0.9);
	font-size: 0.875rem;

	&:not(:last-child) {
		border-bottom: 1px solid rgba(255, 255, 255, 0.1);
	}

	strong {
		color: rgba(255, 255, 255, 0.6);
		margin-right: 0.5rem;
	}
`;

const WarningText = styled.p`
	margin: 0;
	font-size: 0.875rem;
	display: flex;
	align-items: center;
	gap: 0.25rem;

	svg {
		flex-shrink: 0;
	}
`;

const VideoPlayerSection = ({
	drawMode = false,
	onDrawModeChange,
	startPoint = null,
	onStartPointChange,
	endPoint = null,
	onEndPointChange,
	lineColor = '#198754',
}) => {
	const { videoRef, videoURL, videoMetadata, setVideoMetadata, videoFile } =
		useVideoProjectCreation();
	const { watch, setValue } = useFormContext();
	const [videoError, setVideoError] = useState(null);
	const [canPlayVideo, setCanPlayVideo] = useState(true);

	useEffect(() => {
		setVideoError(null);
		setCanPlayVideo(true);
	}, [videoURL]);

	const countingMethod = watch('countingMethod');

	if (!videoURL) {
		return (
			<VideoError>
				<p>No video file selected</p>
			</VideoError>
		);
	}

	const handleLoadedMetadata = () => {
		const video = videoRef.current;
		if (!video) return;

		try {
			const metadata = {
				...extractVideoMetadata(video),
				fileSize: videoFile?.size || 0,
				fileName: videoFile?.name || '',
				fileType: videoFile?.type || '',
			};

			setVideoMetadata(metadata);
			setCanPlayVideo(true);
			setVideoError(null);

			// Initialize time range to full video duration with 2 decimal places
			if (!watch('timeRange')) {
				setValue('timeRange', {
					start_seconds: parseFloat((0).toFixed(2)),
					end_seconds: parseFloat(video.duration.toFixed(2)),
				});
			}
		} catch (err) {
			console.error('Error loading video metadata:', err);
		}
	};

	const handleVideoError = e => {
		console.error('Video playback error:', e);
		const video = videoRef.current;

		if (video?.error) {
			console.error('Video error code:', video.error.code);
			console.error('Video error message:', video.error.message);

			// Map error codes to user-friendly messages
			const errorMessages = {
				1: 'Video loading was aborted',
				2: 'A network error occurred while loading the video',
				3: 'Video decoding failed - the file may be corrupted',
				4: 'Video format is not supported by your browser',
			};

			const userMessage = errorMessages[video.error.code] || 'Unknown video error';
			setVideoError(userMessage);
			setCanPlayVideo(false);

			// Set basic metadata from file if available
			if (videoFile && !videoMetadata) {
				setVideoMetadata({
					duration: 0,
					width: 0,
					height: 0,
					aspectRatio: 0,
					fileSize: videoFile.size,
					fileName: videoFile.name,
					fileType: videoFile.type,
				});
			}
		}
	};

	// Fallback UI when video can't be played
	if (!canPlayVideo && videoError) {
		return (
			<>
				<VideoError>
					<ErrorIcon>
						<TbVideo size={48} />
					</ErrorIcon>
					<ErrorTitle>Video Preview Unavailable</ErrorTitle>
					<ErrorMessage>{videoError}</ErrorMessage>
					<ErrorHint>
						<strong>You can still proceed:</strong>
						<ul style={{ textAlign: 'left', marginTop: '0.5rem' }}>
							<li>Configure counting scenario and method below</li>
							<li>The video will be uploaded and processed on the server</li>
							<li>Try using Firefox or Safari for video preview support</li>
						</ul>
					</ErrorHint>
					{videoFile && (
						<FileInfoBox>
							<FileInfoItem>
								<strong>File:</strong> {videoFile.name}
							</FileInfoItem>
							<FileInfoItem>
								<strong>Size:</strong> {formatFileSize(videoFile.size)}
							</FileInfoItem>
							<FileInfoItem>
								<strong>Type:</strong> {videoFile.type}
							</FileInfoItem>
						</FileInfoBox>
					)}
				</VideoError>

				{countingMethod?.value === 'line_based' && (
					<VideoError style={{ marginTop: '1rem' }}>
						<WarningText>
							<LuTriangleAlert size={14} aria-hidden="true" />
							Virtual line drawing requires video preview. Please select the
							"Unique Object" method or use a compatible browser.
						</WarningText>
					</VideoError>
				)}
			</>
		);
	}

	return (
		<>
			<VideoCanvasWrapper>
				<ErrorBoundary
					FallbackComponent={({ error }) => (
						<VideoError>
							<p>Failed to load video: {error.message}</p>
						</VideoError>
					)}>
					<StyledVideo
						ref={videoRef}
						src={videoURL}
						controls
						controlsList="nofullscreen nodownload noplaybackrate"
						disablePictureInPicture
						preload="metadata"
						onLoadedMetadata={handleLoadedMetadata}
						onError={handleVideoError}
						onLoadStart={() => console.log('Video load started')}
						onCanPlay={() => console.log('Video can play')}
					/>

					{countingMethod?.value === 'line_based' && (
						<ErrorBoundary
							FallbackComponent={() => (
								<div style={{ padding: '1rem', color: '#dc3545' }}>
									Canvas drawing unavailable
								</div>
							)}>
							<CanvasOverlay
								lineColor={lineColor}
								drawMode={drawMode}
								onDrawModeChange={onDrawModeChange}
								startPoint={startPoint}
								onStartPointChange={onStartPointChange}
								endPoint={endPoint}
								onEndPointChange={onEndPointChange}
							/>
						</ErrorBoundary>
					)}
				</ErrorBoundary>
			</VideoCanvasWrapper>

			<TimeRangeSelector />
		</>
	);
};

export default VideoPlayerSection;
